import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import {
  getCertificateResponsibilityHint,
  getDashboardReassuranceState,
  getTenantRecommendedAction,
  formatCertStatusLabel,
  TENANT_RECOMMENDED_ACTION_LABEL,
} from '../utils/tenantPortalTrust';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from '@/utils/portalNotifications';
import { 
  Home, 
  LogOut, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle,
  Shield,
  Building2,
  Calendar,
  RefreshCw,
  ChevronRight,
  Info,
  Download,
  MessageSquare,
  Send,
  FileText,
  X,
  Wrench
} from 'lucide-react';

/**
 * Derive a friendly display name (first name only) from tenant_name.
 * e.g. "Mike Edes" -> "Mike", "mike@mailinator.com" -> "Mike"
 */
function getTenantFirstName(tenantName) {
  if (!tenantName || typeof tenantName !== 'string') return 'Tenant';
  const s = tenantName.trim();
  if (s.includes('@')) {
    const beforeAt = s.split('@')[0].trim();
    return beforeAt ? beforeAt.charAt(0).toUpperCase() + beforeAt.slice(1).toLowerCase() : 'Tenant';
  }
  const firstWord = s.split(/\s+/)[0];
  return firstWord ? firstWord.charAt(0).toUpperCase() + firstWord.slice(1).toLowerCase() : 'Tenant';
}

/**
 * Tenant Dashboard - Enhanced view for tenants
 *
 * Shows property safety checks, certificate rows, summaries, pack download, and light actions.
 */
const TenantDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedProperty, setExpandedProperty] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [contactForm, setContactForm] = useState({ subject: '', message: '' });
  const [requestForm, setRequestForm] = useState({ certificate_type: '', message: '' });
  const [reportForm, setReportForm] = useState({ description: '', category: 'general' });
  const [reportSeverity, setReportSeverity] = useState('Routine');
  const [reportType, setReportType] = useState('issue'); // 'issue' = report-issue (triage), 'work_order' = report-maintenance
  const [submitting, setSubmitting] = useState(false);

  const reassuranceState = useMemo(
    () => getDashboardReassuranceState(data?.properties),
    [data?.properties]
  );

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/tenant/dashboard');
      setData(response.data);
    } catch (err) {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getComplianceStyles = (status) => {
    switch (status) {
      case 'GREEN':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-700',
          icon: <CheckCircle className="w-6 h-6 text-green-500" />,
          label: 'Safety: up to date'
        };
      case 'AMBER':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-700',
          icon: <AlertTriangle className="w-6 h-6 text-yellow-500" />,
          label: 'Safety: renewals due soon'
        };
      case 'RED':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-700',
          icon: <XCircle className="w-6 h-6 text-red-500" />,
          label: 'Safety: needs attention'
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-700',
          icon: <Clock className="w-6 h-6 text-gray-500" />,
          label: 'Unknown'
        };
    }
  };

  const getCertStatusStyles = (status) => {
    switch (status) {
      case 'COMPLIANT':
        return { color: 'text-green-600', bg: 'bg-green-100', icon: <CheckCircle className="w-4 h-4" /> };
      case 'EXPIRING_SOON':
        return { color: 'text-yellow-600', bg: 'bg-yellow-100', icon: <Clock className="w-4 h-4" /> };
      case 'OVERDUE':
        return { color: 'text-red-600', bg: 'bg-red-100', icon: <XCircle className="w-4 h-4" /> };
      case 'PENDING':
        return { color: 'text-blue-600', bg: 'bg-blue-100', icon: <Clock className="w-4 h-4" /> };
      default:
        return { color: 'text-gray-600', bg: 'bg-gray-100', icon: <Info className="w-4 h-4" /> };
    }
  };

  const handleDownloadPack = async (propertyId) => {
    try {
      const response = await api.get(`/tenant/compliance-pack/${propertyId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `compliance_pack_${propertyId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Property safety pack downloaded.');
    } catch (err) {
      toast.error('Failed to download pack');
    }
  };

  const handleContactLandlord = async (e) => {
    e.preventDefault();
    if (!contactForm.message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/tenant/contact-landlord', {
        property_id: selectedProperty.property_id,
        subject: contactForm.subject || 'Message from Tenant',
        message: contactForm.message
      });
      
      toast.success('Message sent to your landlord!');
      setShowContactModal(false);
      setContactForm({ subject: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestCertificate = async (e) => {
    e.preventDefault();
    if (!requestForm.certificate_type) {
      toast.error('Please select a certificate type');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/tenant/request-certificate', {
        property_id: selectedProperty.property_id,
        certificate_type: requestForm.certificate_type,
        message: requestForm.message
      });
      
      toast.success('Request sent to your landlord', {
        description: 'They will see it in Today and can resolve it by uploading documents.',
      });
      setShowRequestModal(false);
      setRequestForm({ certificate_type: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const openContactModal = (property) => {
    setSelectedProperty(property);
    setShowContactModal(true);
  };

  const openRequestModal = (property) => {
    setSelectedProperty(property);
    setShowRequestModal(true);
  };

  const openReportModal = (property) => {
    setSelectedProperty(property);
    setReportForm({ description: '', category: 'general' });
    setReportSeverity('Routine');
    setReportType('issue');
    setShowReportModal(true);
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    if (!reportForm.description?.trim()) {
      toast.error('Please describe the issue');
      return;
    }
    if (!selectedProperty?.property_id) return;
    setSubmitting(true);
    const descriptionWithSeverity = `[Severity: ${reportSeverity}]\n\n${reportForm.description.trim()}`;
    try {
      if (reportType === 'issue') {
        await api.post('/tenant/report-issue', {
          property_id: selectedProperty.property_id,
          description: descriptionWithSeverity,
          category: reportForm.category || undefined,
        });
        toast.success('Issue reported. Your landlord will triage and follow up.');
      } else {
        await api.post('/tenant/report-maintenance', {
          property_id: selectedProperty.property_id,
          description: descriptionWithSeverity,
          category: reportForm.category || undefined,
        });
        toast.success('Repair reported. Your landlord will be notified.');
      }
      setShowReportModal(false);
      setReportForm({ description: '', category: 'general' });
      setReportSeverity('Routine');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = detail && typeof detail === 'object' ? detail.error_code : null;
      if (reportType === 'issue' && err?.response?.status === 409 && code === 'TENANT_DUPLICATE_ISSUE_CONFIRM_REQUIRED') {
        const proceed = window.confirm(
          'A similar open issue already exists. Press OK to create a separate issue, or Cancel to keep tracking the existing issue.',
        );
        if (proceed) {
          try {
            await api.post('/tenant/report-issue', {
              property_id: selectedProperty.property_id,
              description: descriptionWithSeverity,
              category: reportForm.category || undefined,
              confirm_new_issue: true,
            });
            toast.success('New issue created and linked to existing open issue.');
            setShowReportModal(false);
            setReportForm({ description: '', category: 'general' });
            setReportSeverity('Routine');
            return;
          } catch (retryErr) {
            toast.error(retryErr?.response?.data?.detail?.message || retryErr?.response?.data?.detail || 'Failed to report');
            return;
          }
        }
        toast.message('Existing issue kept. No duplicate issue created.');
        return;
      }
      toast.error(detail?.message || detail || 'Failed to report');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="tenant-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-electric-teal" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Alert className="max-w-md">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="tenant-dashboard">
      {/* Header */}
      <header className="bg-midnight-blue text-white py-4">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-electric-teal" />
              <div>
                <h1 className="text-xl font-bold">Compliance Vault</h1>
                <p className="text-sm text-gray-300">Tenant Portal</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-300">{user?.email}</span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                className="bg-transparent border-white/30 text-white hover:bg-white/10"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome & Info Banner */}
        <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-6 mb-8 border border-teal-200">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <Home className="w-6 h-6 text-electric-teal" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-midnight-blue">
                Welcome, {getTenantFirstName(data?.tenant_name)}
              </h2>
              <h3 className="text-base font-semibold text-midnight-blue mt-3">Your property safety status</h3>
              <p className="text-gray-600 mt-1">
                We monitor key safety checks like gas, electrical, and fire systems.
                If anything needs attention, you&apos;ll see it below.
              </p>
              <p className="text-sm text-gray-600 mt-3 leading-snug">
                Tenant requests appear in Today and can be resolved by uploading documents.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Last updated: {new Date(data?.last_updated).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div
          className={`rounded-xl border p-4 mb-8 ${
            reassuranceState.kind === 'overdue'
              ? 'bg-amber-50 border-amber-200 text-amber-900'
              : reassuranceState.kind === 'status_gap'
                ? 'bg-slate-50 border-slate-200 text-slate-800'
                : 'bg-green-50 border-green-200 text-green-900'
          }`}
          data-testid="tenant-reassurance-banner"
        >
          {reassuranceState.kind === 'overdue' && (
            <>
              <p className="font-medium">Some safety checks need attention.</p>
              <p className="text-sm mt-1 opacity-90">
                Renewals are your landlord&apos;s responsibility. They use the same portal to track what is due and can arrange engineers and certificates—you do not need to fix these yourself.
              </p>
            </>
          )}
          {reassuranceState.kind === 'status_gap' && (
            <>
              <p className="font-medium">Overall safety isn&apos;t shown as fully up to date.</p>
              <p className="text-sm mt-1 opacity-90">
                We&apos;re not listing any checks as overdue below—sometimes the summary moves first, or a renewal is due soon.
                Your landlord manages renewals; expand a property for line-by-line detail.
              </p>
            </>
          )}
          {reassuranceState.kind === 'all_clear' && (
            <>
              <p className="font-medium">Your property is currently safe and up to date.</p>
              <p className="text-sm mt-1 opacity-90">
                We are not showing any overdue safety checks for your home right now. If something changes, it will appear here.
              </p>
            </>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <Card data-testid="stat-total-properties">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-midnight-blue">{data?.summary?.total_properties || 0}</div>
              <div className="text-sm text-gray-500">Total Properties</div>
            </CardContent>
          </Card>
          <Card className="border-green-200" data-testid="stat-fully-compliant">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{data?.summary?.fully_compliant || 0}</div>
              <div className="text-sm text-gray-500">All checks OK</div>
            </CardContent>
          </Card>
          <Card className="border-yellow-200" data-testid="stat-needs-attention">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600">{data?.summary?.needs_attention || 0}</div>
              <div className="text-sm text-gray-500">Renewals due soon</div>
            </CardContent>
          </Card>
          <Card className="border-red-200" data-testid="stat-action-required">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{data?.summary?.action_required || 0}</div>
              <div className="text-sm text-gray-500">Safety: needs attention</div>
            </CardContent>
          </Card>
        </div>

        {/* Properties List */}
        <Card data-testid="properties-list">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Your Properties
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.properties?.length === 0 ? (
              <div className="text-center py-8 text-gray-500" data-testid="no-properties">
                <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No properties assigned to you yet.</p>
                <p className="text-sm mt-1">Contact your landlord for access.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {data?.properties?.map((property) => {
                  const styles = getComplianceStyles(property.compliance_status);
                  const isExpanded = expandedProperty === property.property_id;
                  
                  return (
                    <div 
                      key={property.property_id}
                      className={`rounded-lg border-2 ${styles.border} ${styles.bg} overflow-hidden transition-all`}
                      data-testid={`property-${property.property_id}`}
                    >
                      {/* Property Header */}
                      <button
                        onClick={() => setExpandedProperty(isExpanded ? null : property.property_id)}
                        className="w-full p-4 flex items-center justify-between text-left"
                        data-testid={`toggle-property-${property.property_id}`}
                      >
                        <div className="flex items-center gap-4">
                          {styles.icon}
                          <div>
                            <h3 className="font-semibold text-midnight-blue">{property.address}</h3>
                            <p className="text-sm text-gray-500 capitalize">{property.property_type}</p>
                            {(property.compliance_status === 'RED' ||
                              property.compliance_status === 'AMBER') && (
                              <p className="text-xs font-medium text-gray-600 mt-1">
                                Safety status:{' '}
                                {property.compliance_status === 'RED' ? 'Needs attention' : 'Renewals due soon'}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-sm font-medium ${styles.text}`}>
                            {styles.label}
                          </span>
                          <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </div>
                      </button>
                      
                      {/* Certificates List */}
                      {isExpanded && property.certificates && (
                        <div className="border-t bg-white p-4 space-y-3" data-testid={`certificates-${property.property_id}`}>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Safety checks</h4>
                          {property.certificates.length === 0 ? (
                            <p className="text-sm text-gray-500">No certificates on record</p>
                          ) : (
                            property.certificates.map((cert, idx) => {
                              const certStyles = getCertStatusStyles(cert.status);
                              const statusWords = formatCertStatusLabel(cert.status);
                              const respHint = getCertificateResponsibilityHint(cert.status);
                              return (
                                <div 
                                  key={idx}
                                  className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0 gap-2"
                                  data-testid={`cert-${property.property_id}-${idx}`}
                                >
                                  <div className="flex items-start gap-3 min-w-0">
                                    <div className={`p-1.5 rounded shrink-0 ${certStyles.bg} ${certStyles.color}`}>
                                      {certStyles.icon}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="font-medium text-sm text-gray-900">
                                        {cert.description || cert.type}
                                        {' — '}
                                        <span className={certStyles.color}>{statusWords.toUpperCase()}</span>
                                      </p>
                                      <p className="text-xs text-gray-500 mt-0.5">Responsibility: Landlord</p>
                                      {respHint && (
                                        <p className="text-xs text-gray-600 mt-1">{respHint}</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right shrink-0">
                                    {cert.expiry !== 'N/A' && (
                                      <p className="text-xs text-gray-500 flex items-center gap-1 justify-end">
                                        <Calendar className="w-3 h-3" />
                                        Expires: {cert.expiry}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                          
                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200">
                            {(() => {
                              const primaryAction = getTenantRecommendedAction(property.certificates);
                              const primaryClass =
                                'bg-electric-teal hover:bg-teal-600 text-white border-transparent shadow-sm';
                              const outlineClass = 'border-gray-300 text-gray-700 hover:bg-gray-50';
                              return (
                                <>
                                  {primaryAction && (
                                    <p className="w-full text-sm text-midnight-blue font-medium mb-1">
                                      Recommended action: {TENANT_RECOMMENDED_ACTION_LABEL[primaryAction]}
                                    </p>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDownloadPack(property.property_id)}
                                    className={outlineClass}
                                    data-testid={`download-pack-${property.property_id}`}
                                  >
                                    <Download className="w-4 h-4 mr-2" />
                                    Download safety pack
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openRequestModal(property)}
                                    className={
                                      primaryAction === 'request_certificate' ? primaryClass : outlineClass
                                    }
                                    data-testid={`request-cert-${property.property_id}`}
                                  >
                                    <FileText className="w-4 h-4 mr-2" />
                                    Request certificate
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openContactModal(property)}
                                    className={
                                      primaryAction === 'contact_landlord' ? primaryClass : outlineClass
                                    }
                                    data-testid={`contact-landlord-${property.property_id}`}
                                  >
                                    <MessageSquare className="w-4 h-4 mr-2" />
                                    Contact landlord
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openReportModal(property)}
                                    className={outlineClass}
                                    data-testid={`report-repair-${property.property_id}`}
                                  >
                                    <Wrench className="w-4 h-4 mr-2" />
                                    Report a repair
                                  </Button>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Footer */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-800">About this portal</h4>
              <p className="text-sm text-blue-700 mt-1">
                This is a simple view of safety checks for your home. You can download a summary pack,
                ask for certificate updates, report repairs, or message your landlord if you are unsure.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Contact Landlord Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="contact-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-midnight-blue flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-electric-teal" />
                Contact Landlord
              </h3>
              <button 
                onClick={() => setShowContactModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleContactLandlord} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property</label>
                <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                  {selectedProperty?.address}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={contactForm.subject}
                  onChange={(e) => setContactForm(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-electric-teal focus:border-transparent"
                  placeholder="What's this about?"
                  data-testid="contact-subject"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-electric-teal focus:border-transparent h-32 resize-none"
                  placeholder="Type your message here..."
                  maxLength={1000}
                  required
                  data-testid="contact-message"
                />
                <p className="text-xs text-gray-500 mt-1">{contactForm.message.length}/1000</p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowContactModal(false)}
                  className="flex-1"
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-electric-teal hover:bg-teal-600"
                  disabled={submitting}
                  data-testid="send-contact-btn"
                >
                  {submitting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Request Certificate Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="request-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-midnight-blue flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Request certificate update
              </h3>
              <button 
                onClick={() => setShowRequestModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleRequestCertificate} className="p-4 space-y-4">
              <p className="text-sm text-gray-600 leading-snug -mt-1">
                This request appears in your landlord&apos;s Today inbox. They can clear it by uploading the certificate or
                related document.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property</label>
                <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                  {selectedProperty?.address}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Certificate Type *</label>
                <select
                  value={requestForm.certificate_type}
                  onChange={(e) => setRequestForm(prev => ({ ...prev, certificate_type: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="request-cert-type"
                >
                  <option value="">Select certificate type...</option>
                  <option value="gas_safety">Gas Safety Certificate (CP12)</option>
                  <option value="eicr">EICR (Electrical)</option>
                  <option value="epc">Energy Performance Certificate</option>
                  <option value="fire_alarm">Fire Alarm Certificate</option>
                  <option value="smoke_co_alarm">Smoke & CO Alarm Certificate</option>
                  <option value="legionella">Legionella Risk Assessment</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
                <textarea
                  value={requestForm.message}
                  onChange={(e) => setRequestForm(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24 resize-none"
                  placeholder="Why do you need this certificate? (optional)"
                  maxLength={500}
                  data-testid="request-message"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowRequestModal(false)}
                  className="flex-1"
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={submitting}
                  data-testid="submit-request-btn"
                >
                  {submitting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500 text-center">
                Your landlord will be notified of this request via email.
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Report a repair / issue modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="report-repair-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-midnight-blue flex items-center gap-2">
                <Wrench className="w-5 h-5 text-amber-600" />
                Report maintenance
              </h3>
              <button
                onClick={() => setShowReportModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleReportSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property</label>
                <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                  {selectedProperty?.address}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Report as</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="reportType"
                      checked={reportType === 'issue'}
                      onChange={() => setReportType('issue')}
                      className="text-amber-600"
                    />
                    <span className="text-sm">Issue (recommended)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="reportType"
                      checked={reportType === 'work_order'}
                      onChange={() => setReportType('work_order')}
                      className="text-amber-600"
                    />
                    <span className="text-sm">Job</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {reportType === 'issue' ? 'Creates an issue for your landlord to triage and assign.' : 'Creates a job directly.'}
                </p>
              </div>
              {reportType === 'issue' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={reportForm.category}
                    onChange={(e) => setReportForm((prev) => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  >
                    <option value="general">General</option>
                    <option value="plumbing">Plumbing</option>
                    <option value="electrical">Electrical</option>
                    <option value="heating">Heating</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">How urgent is this?</label>
                <select
                  value={reportSeverity}
                  onChange={(e) => setReportSeverity(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  data-testid="report-severity"
                >
                  <option value="Emergency">Emergency</option>
                  <option value="Urgent">Urgent</option>
                  <option value="Routine">Routine</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Describe the issue *</label>
                <textarea
                  value={reportForm.description}
                  onChange={(e) => setReportForm((prev) => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent h-32 resize-none"
                  placeholder="e.g. Boiler not heating, leak under sink..."
                  required
                  data-testid="report-repair-description"
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  Use this to report issues like:
                  <span className="block mt-0.5">— Heating not working</span>
                  <span className="block">— Water leaks</span>
                  <span className="block">— Electrical faults</span>
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowReportModal(false)}
                  className="flex-1"
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  disabled={submitting}
                  data-testid="submit-report-repair-btn"
                >
                  {submitting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Wrench className="w-4 h-4 mr-2" />
                      Submit
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500 text-center">
                {reportType === 'issue'
                  ? 'Your landlord will see this in Operations → Issues and can triage and create a job.'
                  : 'A job will be created. Your landlord can assign a contractor and track the repair.'}
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantDashboard;
